import { useTranslation } from 'react-i18next';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { AddProviders } from './AddProviders/';
import {
  AdditionalPlanDataDto,
  DependentFormDataDto,
  PcpDrawerProps,
} from '../../types';
import { DependentDto } from 'theSource/features/FamilyMembers/types';
import { PcpMembersDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { validatePcpRequiredFields } from '../../utils';
import { ChoosePCPDrawer } from '../ChoosePCPDrawer/ChoosePCPDrawer';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import { setPcpDentalData } from '../ChoosePCPDrawer/SearchResultsPCPDrawer/services';
import { closeSideDrawer } from 'enrollment/utils/common/Utils';
import { usePageContentQuery } from 'enrollment/queries/usePageContentQuery';

export const PCPDrawer = ({
  plan,
  subscriber,
  dependents,
  selectedDependentIds,
  togglePcpDrawer,
  setPcpElectionData,
  pcpMembers,
  benefitLookup,
  showPcpDrawer,
  openChooseProviderDrawer,
  setOpenChooseProviderDrawer,
  setShowPcpDrawer,
  setPcpDrawerRequired,
  isFindAProviderEnabled,
  hideDependentCheckboxWhenChecked,
  setHideDependentCheckboxWhenChecked,
  subscribeZipCode,
  setDisplayPopups,
}: PcpDrawerProps) => {
  const { t } = useTranslation('enrollment.pcpDrawer');
  const dispatch = useDispatch();
  const isPcpNameRequired = plan?.pcp_data?.pcp_name_setting === 'required';
  const isPcpCodeRequired = plan?.pcp_data?.pcp_code_setting === 'required';
  const isPcpNameNone = plan?.pcp_data?.pcp_name_setting === '';
  const isPcpCodeNone = plan?.pcp_data?.pcp_code_setting === '';

  const hasRequiredFields = isPcpNameRequired || isPcpCodeRequired;
  const isMedicalBenefit = benefitLookup === 'medical';

  const [additionalPlanData, setAdditionalPlanData] =
    useState<AdditionalPlanDataDto>();
  const [isSaveDisabled, setIsMissingRequiredFields] = useState<boolean>(false);

  const buildDependentsPcpData = (
    selectedDependents: DependentDto[] | undefined,
    pcpMembers: PcpMembersDto | undefined
  ) => {
    const arrReturn: DependentFormDataDto[] = [];
    selectedDependents?.forEach((selectedDependent) => {
      const existingDependent = pcpMembers?.dependents.find(
        (pcpMemberDep) => selectedDependent.id === pcpMemberDep.id
      );
      if (existingDependent) {
        arrReturn.push({
          id: selectedDependent.id,
          pcpName: existingDependent.pcp_name,
          pcpCode: existingDependent.pcp_code,
          codeOptions: existingDependent.code_options,
          nameOptions: existingDependent.name_options,
          existingPatient: existingDependent.existing_patient,
          firstName: selectedDependent.first_name,
          birthDate: selectedDependent.birthdate,
          dependentProfile: selectedDependent.dependent_profile,
        });
      } else {
        // build default form state object for new selected dependent
        arrReturn.push({
          id: selectedDependent.id,
          pcpName: '',
          pcpCode: '',
          existingPatient: false,
          firstName: selectedDependent.first_name,
          dependentProfile: selectedDependent.dependent_profile,
        });
      }
    });

    return arrReturn;
  };

  useEffect(() => {
    const selectedDependents = dependents?.data.dependents.filter((dependent) =>
      selectedDependentIds?.includes(dependent.id)
    ) as DependentDto[] | undefined;

    setAdditionalPlanData({
      pcpName: pcpMembers?.pcp_name || '',
      pcpCode: pcpMembers?.pcp_code || '',
      existingPatient: pcpMembers?.existing_patient || false,
      subscriberProfile: subscriber,
      dependents: selectedDependents
        ? buildDependentsPcpData(selectedDependents, pcpMembers)
        : [],
    });
  }, [subscriber, dependents, selectedDependentIds, pcpMembers]);

  const handlePcpFormChange = (
    isSubscriber = false,
    event: React.ChangeEvent<HTMLInputElement>,
    dependentId: number
  ) => {
    const targetElement = event.target as HTMLInputElement;
    let fieldValue = '' as string | boolean;
    switch (targetElement.name) {
      case 'pcpName':
      case 'pcpCode':
        fieldValue = targetElement.value;
        break;
      case 'existingPatient':
        fieldValue = targetElement.checked ? true : false;
        break;
      default:
        break;
    }
    if (isSubscriber) {
      setAdditionalPlanData((prevState: any) => ({
        ...prevState,
        [targetElement.name]: fieldValue,
      }));
    } else {
      setAdditionalPlanData((prevState: any) => {
        const updatedDependents = prevState.dependents?.map(
          (dependent: DependentFormDataDto) =>
            dependent.id === dependentId
              ? { ...dependent, [targetElement.name]: fieldValue }
              : dependent
        );

        return {
          ...prevState,
          dependents: updatedDependents,
        };
      });
    }
  };

  const validateFormFields = useCallback(() => {
    if (additionalPlanData) {
      const hasMissingField = validatePcpRequiredFields(
        additionalPlanData,
        isPcpNameRequired,
        isPcpCodeRequired
      );

      setIsMissingRequiredFields(hasMissingField);
    }
  }, [additionalPlanData, isPcpNameRequired, isPcpCodeRequired]);

  useEffect(() => {
    if (hasRequiredFields && additionalPlanData) {
      validateFormFields();
    }
  }, [hasRequiredFields, additionalPlanData, validateFormFields]);

  const pcpDrawerHeading = isMedicalBenefit
    ? t('heading')
    : t('dental.heading');

  const handleSubmit = () => {
    dispatch(setPcpDentalData(additionalPlanData));
    setPcpElectionData({
      pcpFormData: additionalPlanData,
    });
    setPcpDrawerRequired(false);
  };

  const handleDrawerClose = () => {
    togglePcpDrawer();
    closeSideDrawer();
    if (setDisplayPopups) {
      setDisplayPopups(false);
    }
  };

  const { pageContent } = usePageContentQuery({
    pageContentLookupCode: 'enroll_pcp',
    benefitLookupCode: benefitLookup,
    orgPlanIds: plan?.plan_id?.toString(),
  });

  return (
    <OrbitDrawer
      dialogLarge
      heading={pcpDrawerHeading}
      aria-label="pcpDrawer"
      drawerId="pcpDrawer"
      isOpen={showPcpDrawer}
      footer={
        isMedicalBenefit || (!isFindAProviderEnabled && !isMedicalBenefit)
      }
      saveButton={t('common:save')}
      saveButtonDisabled={isSaveDisabled}
      handleClose={handleDrawerClose}
      handleSubmit={handleSubmit}
      hTagImg={plan?.carrier_logo_url ? plan.carrier_logo_url : ''}
      hTagImgAlt={plan?.carrier}
    >
      {isMedicalBenefit || (!isFindAProviderEnabled && !isMedicalBenefit) ? (
        <AddProviders
          pageText={pageContent}
          subscriber={subscriber}
          dependents={dependents?.data?.dependents}
          pcpFormData={additionalPlanData}
          handlePcpFormChange={handlePcpFormChange}
          isPcpNameRequired={isPcpNameRequired}
          isPcpCodeRequired={isPcpCodeRequired}
          isPcpNameNone={isPcpNameNone}
          isPcpCodeNone={isPcpCodeNone}
          data-testid="addProviders"
        />
      ) : (
        <div>
          <div>
            {isPcpNameRequired || isPcpCodeRequired
              ? t('dental.descriptionMandate')
              : t('dental.descriptionOptional')}
          </div>
          <h3 className="mt-5">{t('dental.searchProviderLabel')}</h3>
          <ChoosePCPDrawer
            setPcpElectionData={setPcpElectionData}
            setPcpDrawerRequired={setPcpDrawerRequired}
            selectedDependentIds={selectedDependentIds}
            openChooseProviderDrawer={openChooseProviderDrawer}
            setOpenChooseProviderDrawer={setOpenChooseProviderDrawer}
            setShowPcpDrawer={setShowPcpDrawer}
            plan={plan}
            hideDependentCheckboxWhenChecked={hideDependentCheckboxWhenChecked}
            setHideDependentCheckboxWhenChecked={
              setHideDependentCheckboxWhenChecked
            }
            subscribeZipCode={subscribeZipCode}
            pageContent={pageContent}
          />
        </div>
      )}
    </OrbitDrawer>
  );
};
